import "./form-route.scss";

import * as React from "react";
import {Types}    from "@Common/types";

import {cs, cst, cstAssign}  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {Load2}               from "@Common/react/load2";
import {Static2}             from "@Common/react/static-2";
import {UseState}            from "@Common/react/use-state";
import {AlertModal}          from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {ConfirmDialog}       from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {changeNestedValue}   from "@Common/utils/arr-path";
import {back}                from "@Common/utils/browser/back";
import {filterKeys}          from "@Common/utils/objects";
import {parseUrlQuery}       from "@Common/utils/parse-url-query";

import {AccountLayout}        from "@Xpresso/routes/account/common/account-layout/account-layout";
import {ID_PROGRAM_TYPE}    from "@Xpresso/routes/common/logic/program";
import {COMPANY_LEVEL}        from "@Xpresso/routes/common/logic/user-level";
import {MyListingsErrorModal} from "@Xpresso/routes/my-listings/my-listings-error-modal/my-listings-error-modal";

import {mapApiModelForForm, mapFormModelForApi} from "./api-tools";
import {ConfirmationModal}  from "./confirmation-modal";
import {ListingFormContent} from "./form-content";
import {ListingFormState}   from "./form-state";

type FormListingModel    = Types.Xpresso.Account.Admin.Listings.ListingModel;
type PrefilledModel      = {[k in typeof PREFILLED_FIELDS_ALLOWED[number]]: string};

const PREFILLED_FIELDS_ALLOWED = [
    "city",
    "id",
    "mlsId",
    "state",
    "streetAddress",
    "unit",
    "zip"
] as const;

interface VarStack {
    alertModal           : Types.Common.UiComponents.Heavy.Common.Design2.Dialogs.AlertModal.Next,
    apis                 : Types.Apis.Loaders.XpressoLoadApis,
    routerHistory        : Types.Xpresso.Loaders.ContextNameMap['routerHistory'],
    confirmationModal    : Types.Xpresso.Account.Admin.Listings.ListingFormConfirmationModalNext,
    errorRefStatic       : Types.Common.React.Static2.Static2Next<HTMLElement>,
    formState            : Types.Xpresso.Account.Admin.Listings.ListingFormStateNext,
    hasApm2ProgramLoad   : Types.Common.React.Load2.Load2Next<boolean>,
    listingLoad          : Types.Common.React.Load2.Load2Next<FormListingModel>,
    listingId            : string,
    routeState           : Types.Common.React.UseState.UseStateNext<{successMessage: string, validating: boolean}>,
    routeQuery           : Record<string, string>,
    sectionsVisibleState : Types.Common.React.UseState.UseStateNext<Types.Xpresso.Account.Admin.Listings.ListingFormContentVisibleSectionsModel>,
};

export const AccountAdminListingsFormRoute = () => cst<{
    session              : Types.Xpresso.Loaders.ContextNameMap['session'],
    userLevels           : Types.Xpresso.Loaders.ContextNameMap['userLevels'],
    myListingsErrorModal : any,
}>(
    ({}, next) => AccountLayout({next, active: "listingsAdmin"}),
    consumeContext("session"),
    consumeContext("userLevels"),
    ["myListingsErrorModal", (_, next) => MyListingsErrorModal({back:true, isCC:true, next})],
    ({session, userLevels, myListingsErrorModal}) => (userLevels.userLevel === COMPANY_LEVEL || session?.data?.branch?.idBranch) && cst<VarStack>(
        consumeContext("apis"),
        cstAssign("alertModal", ({}, next) => AlertModal({next})),
        consumeContext("routerHistory"),

        cstAssign("hasApm2ProgramLoad", ({apis}, next) => Load2({
            next,
            fetch: async () => {
                const programs = await apis.next.program.getList();
                return programs.some(program => program.active && program.idProgramType === ID_PROGRAM_TYPE.APM2);
            },
        })),
        cstAssign("routeQuery", ({}, next) => next(parseUrlQuery(window.location.search))),
        cstAssign("listingId", ({routeQuery}, next) => next(routeQuery?.id)),
        cstAssign("listingLoad", ({apis, routeQuery}, next) => Load2({
            next,
            fetch: async () => {
                const prefilled:PrefilledModel = routeQuery ? filterKeys(
                    routeQuery,
                    (key) => PREFILLED_FIELDS_ALLOWED.includes(key as keyof PrefilledModel)
                ) as PrefilledModel : null;

                if (prefilled?.id) {
                    const listing = await apis.listings.getListing({id: prefilled.id});
                    if (listing)
                        return mapApiModelForForm(listing);
                    else {
                        await myListingsErrorModal.show();
                        return null;
                    }
                } else
                    return {
                        branch                : session?.data?.branch,
                        locationCity          : prefilled?.city,
                        locationState         : prefilled?.state,
                        locationStreetAddress : prefilled?.streetAddress,
                        locationUnitNumber    : prefilled?.unit,
                        locationZip           : prefilled?.zip,
                        mlsId                 : prefilled?.mlsId,
                    };
            },
        })),
        ({listingLoad}, next) => listingLoad.loading ? "Loading..." : next(),

        cstAssign("errorRefStatic", ({}, next) => Static2({next})),
        cstAssign("confirmationModal", ({}, next) => ConfirmationModal({next})),
        cstAssign("routeState", ({}, next) => UseState({next, initValue: {successMessage: null, validating: false}})),
        cstAssign("formState", ({hasApm2ProgramLoad, listingLoad: {value}}, next) => ListingFormState({next, value, apmSupport: hasApm2ProgramLoad.value})),

        cstAssign("sectionsVisibleState", ({}, next) => UseState({next, initValue: {'basic-details': true, 'location-data': true, 'property-photos': true}})),
        ({
            alertModal,
            apis,
            confirmationModal,
            errorRefStatic: {get: getErrorRef, set: setErrorRef},
            formState,
            hasApm2ProgramLoad: {value: hasApm2Program},
            listingId,
            routeState: {onChange: setRouteState, value: {successMessage, validating}},
            sectionsVisibleState: {onChange: setSectionsVisible, value: sectionsVisible},
            routerHistory,
        }) => (
            <div className="account-admin-listings-form-route-2igv">
                {successMessage && <p className="success">{successMessage}</p>}
                <ListingFormContent
                    apmSupport={hasApm2Program}
                    errorRef={setErrorRef}
                    fields={formState.fields}
                    validating={validating}
                    sectionsVisible={sectionsVisible}

                    onSectionSwitch={(section) => setSectionsVisible(changeNestedValue(sectionsVisible, [section], (val) => !val))}
                />
                <div className="footer-ui">

                    {cs(
                        ["confirmDialog", (_, next) => ConfirmDialog({next})],
                        ({confirmDialog}) => (
                            <button
                                onClick={async () => {
                                    if (await confirmDialog.show({
                                        actionName: "discard",
                                        message: "Discard new Listing?",
                                    })) {
                                        await back({fallbackAction: async () => routerHistory.push("/account/admin/listing-recipient-admin")});
                                    }
                                }}
                                xd-qa-type="button"
                                xd-qa-name="Cancel"
                            >
                                Cancel
                            </button>
                        ),
                    )}

                    <button
                        disabled={validating && !formState.valid}
                        onClick={async () => {
                            setRouteState({successMessage: "", validating: true});

                            if (formState.valid) {
                                const branch = userLevels.userLevel === COMPANY_LEVEL ? formState.selectedBranch : session.data.branch;
                                confirmationModal.show({
                                    branch,
                                    agent        : formState.selectedAgent,
                                    agentAddress : {
                                        address1   : branch.branchAddress1,
                                        city       : branch.branchCity,
                                        state      : branch.branchState,
                                        postalCode : branch.branchZip,
                                    },
                                    confirmLabel : listingId ? "Update Listing" : "Create New Listing",
                                    listing      : formState.value,
                                    title        : listingId ? "Listing Update Verify" : "New Listing Verify",

                                    onConfirm: makeConfirmationHandler({alertModal, apis, confirmationModal, formState, listingId, hasApm2Program, setRouteState, routerHistory}),
                                });
                            } else {
                                setSectionsVisible(
                                    {'basic-details': true, 'location-data': true, 'property-photos': true},
                                    () => getErrorRef()?.scrollIntoView({behavior: "smooth"})
                                );
                            }
                        }}
                        xd-qa-type="button"
                        xd-qa-name="Verify"
                        className="primary"
                    >
                        Verify
                    </button>
                </div>
            </div>
        ),
    ),
);

const makeConfirmationHandler = ({alertModal, apis, confirmationModal, formState: {value: listing}, hasApm2Program, listingId, setRouteState, routerHistory}:(
    Pick<VarStack, "alertModal" | "apis" | "confirmationModal" | "formState" | "listingId">
    & {
        hasApm2Program : boolean,
        setRouteState  : VarStack['routeState']['onChange'],
        routerHistory  : any
    }
)):() => Promise<void> => (
    async () => {
        try {
            let successMessage:string;

            if (listingId) {
                await apis.listings.updateListing({
                    listingId,
                    listingData : mapFormModelForApi(listing),
                    buyerSeller : {
                        primaryBuyerFirstName : listing.primaryBuyerFirstName,
                        primaryBuyerLastName  : listing.primaryBuyerLastName,
                    },
                    event       : {
                        generate    : hasApm2Program,
                        notifyAgent : listing.eventNotifyAgent,
                    },
                });
                successMessage = "Listing updated successfully";
            } else {
                const newListingId = await apis.listings.createListing({
                    listing     : mapFormModelForApi(listing),
                    buyerSeller : {
                        primaryBuyerFirstName : listing.primaryBuyerFirstName,
                        primaryBuyerLastName  : listing.primaryBuyerLastName,
                    },
                    event       : {
                        generate    : hasApm2Program,
                        notifyAgent : listing.eventNotifyAgent,
                    },
                });

                routerHistory.replace(`${routerHistory.location.pathname}?id=${encodeURIComponent(newListingId)}`);

                successMessage = "Listing created successfully";
            }

            setRouteState({successMessage: successMessage, validating: false});
            confirmationModal.show(null);
            window.scrollTo({top: 0, behavior: "smooth"});
        } catch (error) {
            if (error?.status === 409) {
                await alertModal.show({message: "Duplicate Listing", description: "This listing is duplicating another; please update the existing listing instead."});
            } else {
                console.error(error);
                await alertModal.show({message: "Error", description: "An error occurred while saving your listing. Please verify your input and try again"});
            }
        }
    }
);

